import React, { useState } from 'react';
import { X } from 'lucide-react';
import { adminFetch } from '@/utils/adminAuth';
import { toast } from 'sonner';

const Field = ({ label, children }) => (
  <div className="mb-3">
    <label className="text-xs text-gray-400 mb-1 block">{label}</label>
    {children}
  </div>
);

const inputCls =
  'w-full bg-[#0a0a0a] border border-[#333] rounded-lg px-3 py-2 text-sm focus:border-amber-500 focus:outline-none';

const Modal = ({ title, onClose, children }) => (
  <div
    className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-4"
    onClick={onClose}
    data-testid="sample-store-modal-backdrop"
  >
    <div
      className="bg-[#141414] border border-[#333] rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold">{title}</h3>
        <button
          onClick={onClose}
          className="p-1 rounded hover:bg-white/10"
          data-testid="sample-store-modal-close"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      {children}
    </div>
  </div>
);

export const AddSampleModal = ({ onClose, onCreated, categories = [], instruments = [], genres = [] }) => {
  const [form, setForm] = useState({
    name: '',
    description: '',
    category: categories[0]?.id || 'one_shot',
    subcategory: genres[0]?.id || 'samba',
    instrument: instruments[0]?.id || 'surdo',
    price: '0.99',
    bpm: '',
    duration_bars: '',
    tags: '',
    is_featured: false,
  });
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [image, setImage] = useState(null);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name) return toast.error('Name is required');
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (file) fd.append('file', file);
      if (preview) fd.append('preview', preview);
      if (image) fd.append('image', image);
      const res = await adminFetch('/sample-store/products', { method: 'POST', body: fd });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed');
      toast.success('Sample created');
      onCreated?.();
      onClose();
    } catch (err) {
      toast.error(err.message || 'Failed to create sample');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Add Sample" onClose={onClose}>
      <form onSubmit={submit} data-testid="add-sample-form">
        <Field label="Name">
          <input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="add-sample-name" />
        </Field>
        <Field label="Description">
          <textarea rows={2} className={inputCls} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="add-sample-description" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Category">
            <select className={inputCls} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="add-sample-category">
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
          <Field label="Instrument">
            <select className={inputCls} value={form.instrument} onChange={(e) => setForm({ ...form, instrument: e.target.value })} data-testid="add-sample-instrument">
              {instruments.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
            </select>
          </Field>
          <Field label="Genre">
            <select className={inputCls} value={form.subcategory} onChange={(e) => setForm({ ...form, subcategory: e.target.value })} data-testid="add-sample-genre">
              {genres.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          </Field>
          <Field label="Price (USD)">
            <input type="number" step="0.01" min="0" className={inputCls} value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="add-sample-price" />
          </Field>
          <Field label="BPM (optional)">
            <input type="number" className={inputCls} value={form.bpm} onChange={(e) => setForm({ ...form, bpm: e.target.value })} data-testid="add-sample-bpm" />
          </Field>
          <Field label="Duration (bars)">
            <input type="number" className={inputCls} value={form.duration_bars} onChange={(e) => setForm({ ...form, duration_bars: e.target.value })} data-testid="add-sample-duration" />
          </Field>
        </div>
        <Field label="Tags (comma-separated)">
          <input className={inputCls} value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} data-testid="add-sample-tags" />
        </Field>
        <Field label="Audio file (WAV/MP3)">
          <input type="file" accept="audio/*" onChange={(e) => setFile(e.target.files?.[0])} data-testid="add-sample-file" className="text-xs" />
        </Field>
        <Field label="Preview MP3 (optional)">
          <input type="file" accept="audio/*" onChange={(e) => setPreview(e.target.files?.[0])} data-testid="add-sample-preview" className="text-xs" />
        </Field>
        <Field label="Cover image (optional)">
          <input type="file" accept="image/*" onChange={(e) => setImage(e.target.files?.[0])} data-testid="add-sample-image" className="text-xs" />
        </Field>
        <label className="flex items-center gap-2 text-sm text-gray-300 mb-4">
          <input type="checkbox" checked={form.is_featured} onChange={(e) => setForm({ ...form, is_featured: e.target.checked })} data-testid="add-sample-featured" />
          Mark as featured
        </label>
        <div className="flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-sm" data-testid="add-sample-cancel">Cancel</button>
          <button type="submit" disabled={saving} className="px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-black font-semibold text-sm disabled:opacity-50" data-testid="add-sample-submit">
            {saving ? 'Saving…' : 'Create Sample'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export const AddCategoryModal = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({ id: '', name: '', description: '', icon: 'Music', price_range: '' });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.id || !form.name) return toast.error('ID and Name are required');
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      const res = await adminFetch('/sample-store/categories', { method: 'POST', body: fd });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed');
      toast.success('Category created');
      onCreated?.();
      onClose();
    } catch (err) {
      toast.error(err.message || 'Failed to create category');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Add Category" onClose={onClose}>
      <form onSubmit={submit} data-testid="add-category-form">
        <Field label="ID (slug, e.g. 'drum_fills')">
          <input className={inputCls} value={form.id} onChange={(e) => setForm({ ...form, id: e.target.value.replace(/\s+/g, '_').toLowerCase() })} data-testid="add-category-id" />
        </Field>
        <Field label="Name">
          <input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="add-category-name" />
        </Field>
        <Field label="Description">
          <textarea rows={2} className={inputCls} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="add-category-description" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Icon">
            <select className={inputCls} value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} data-testid="add-category-icon">
              {['Zap', 'Repeat', 'Repeat1', 'Layers', 'LayoutGrid', 'Music', 'Package'].map((i) => <option key={i} value={i}>{i}</option>)}
            </select>
          </Field>
          <Field label="Price range (e.g. $0.99 - $2.99)">
            <input className={inputCls} value={form.price_range} onChange={(e) => setForm({ ...form, price_range: e.target.value })} data-testid="add-category-price-range" />
          </Field>
        </div>
        <div className="flex items-center justify-end gap-2 mt-4">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-sm" data-testid="add-category-cancel">Cancel</button>
          <button type="submit" disabled={saving} className="px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-black font-semibold text-sm disabled:opacity-50" data-testid="add-category-submit">
            {saving ? 'Saving…' : 'Create Category'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export const AddPackModal = ({ onClose, onCreated, products = [] }) => {
  const [form, setForm] = useState({ name: '', description: '', regular_price: '', pack_price: '' });
  const [selectedIds, setSelectedIds] = useState([]);
  const [image, setImage] = useState(null);
  const [saving, setSaving] = useState(false);

  const toggleSample = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name) return toast.error('Name is required');
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append('sample_ids', selectedIds.join(','));
      if (image) fd.append('image', image);
      const res = await adminFetch('/sample-store/packs', { method: 'POST', body: fd });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed');
      toast.success('Pack created');
      onCreated?.();
      onClose();
    } catch (err) {
      toast.error(err.message || 'Failed to create pack');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Add Pack" onClose={onClose}>
      <form onSubmit={submit} data-testid="add-pack-form">
        <Field label="Name">
          <input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="add-pack-name" />
        </Field>
        <Field label="Description">
          <textarea rows={2} className={inputCls} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="add-pack-description" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Regular price (USD)">
            <input type="number" step="0.01" className={inputCls} value={form.regular_price} onChange={(e) => setForm({ ...form, regular_price: e.target.value })} data-testid="add-pack-regular-price" />
          </Field>
          <Field label="Pack price (USD)">
            <input type="number" step="0.01" className={inputCls} value={form.pack_price} onChange={(e) => setForm({ ...form, pack_price: e.target.value })} data-testid="add-pack-pack-price" />
          </Field>
        </div>
        <Field label="Cover image (optional)">
          <input type="file" accept="image/*" onChange={(e) => setImage(e.target.files?.[0])} data-testid="add-pack-image" className="text-xs" />
        </Field>
        <Field label={`Included samples (${selectedIds.length} selected)`}>
          <div className="max-h-40 overflow-y-auto bg-[#0a0a0a] border border-[#333] rounded-lg p-2 text-sm">
            {products.length === 0 && <p className="text-gray-500 text-xs">No samples yet — add some first.</p>}
            {products.map((p) => (
              <label key={p.id} className="flex items-center gap-2 py-1 cursor-pointer hover:bg-white/5 rounded px-1">
                <input
                  type="checkbox"
                  checked={selectedIds.includes(p.id)}
                  onChange={() => toggleSample(p.id)}
                  data-testid={`add-pack-sample-${p.id}`}
                />
                <span className="truncate">{p.name}</span>
                <span className="ml-auto text-xs text-amber-400">${p.price}</span>
              </label>
            ))}
          </div>
        </Field>
        <div className="flex items-center justify-end gap-2 mt-4">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-sm" data-testid="add-pack-cancel">Cancel</button>
          <button type="submit" disabled={saving} className="px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-black font-semibold text-sm disabled:opacity-50" data-testid="add-pack-submit">
            {saving ? 'Saving…' : 'Create Pack'}
          </button>
        </div>
      </form>
    </Modal>
  );
};
